<script setup lang="ts">
import {
  callOnce,
  computed,
  defineOgImageComponent,
  useHead,
  usePlaces,
  useRoute,
  useSpots,
  watch,
  useHeaderStyle,
} from "#imports";
import MPSectionHeader from "~/components/layouts/MPSectionHeader.vue";
import MPCategoryList from "~/components/widgets/MPCategoryList.vue";
import MPCardCarousel from "~/components/widgets/MPCardCarousel.vue";
import MPSpotCard from "~/components/layouts/MPSpotCard.vue";
import MPMapCard from "~/components/layouts/MPMapCard.vue";
import { uniqBy } from "lodash-es";

useHeaderStyle("opaque");

const route = useRoute();
const placeStore = usePlaces();
const spotStore = useSpots();

const spots = computed(() => spotStore.spotByPlace!);
const place = computed(
  () => placeStore.getById(route.params.id as string).value!,
);

const loadData = async () => {
  if (typeof route.params.id !== "string") {
    return;
  }
  await spotStore.fetchByPlace({ placeId: route.params.id, limit: 20 });
  await placeStore.fetchById(route.params.id);
};

await callOnce(loadData, { mode: "navigation" });
watch(() => route.params.id, loadData);

const categories = computed(() => {
  const cats = spots.value
    ?.map((spot) => spot.categories)
    .filter((cat) => !!cat);
  return uniqBy(cats, "id");
});

const maps = computed(() => {
  const maps = spots.value?.map((spot) => spot.maps).filter((map) => !!map);
  return uniqBy(maps, "id");
});

useHead(() => ({
  title: `${place.value.place} - ${place.value.regionCountry}`,
  meta: [
    {
      name: "description",
      content: "Explore the world and discover new places to visit",
    },
  ],
}));

defineOgImageComponent("Default", {
  title: `${place.value.place} - ${place.value.regionCountry}`,
  description: "Explore the world and discover new places to visit",
  imagePath: place.value.cover_image_url,
});
</script>

<template>
  <div class="destination">
    <MPSectionHeader
      :title="`Explore curated Spots in ${place.fullName} by Local experts`"
      summary="Unlock insights from real creators, discover hidden gems and secret spots that go beyond the usual travel guides."
      :subtitle="`Your local guide to ${place.fullName}`"
    />
    <div class="categories">
      <MPCategoryList :categories="categories" />
    </div>
    <div
      class="destination-header"
      :style="{ backgroundImage: `url(${place.cover_image_url})` }"
    />
    <section class="destination-carousel-wrapper">
      <div>
        <MPSectionHeader
          :title="`Recently added spots in ${place.fullName}`"
          :summary="`Discover some of the most popular spots in ${place.fullName}`"
        />
        <MPCardCarousel v-if="spots && spots.length > 4" :slidesPerView="5">
          <template #slide-0>
            <MPSpotCard :spotId="spots[0]!.id" />
          </template>
          <template #slide-1>
            <MPSpotCard :spotId="spots[1]!.id" />
          </template>
          <template #slide-2>
            <MPSpotCard :spotId="spots[2]!.id" />
          </template>
          <template #slide-3>
            <MPSpotCard :spotId="spots[3]!.id" />
          </template>
          <template #slide-4>
            <MPSpotCard :spotId="spots[4]!.id" />
          </template>
        </MPCardCarousel>
      </div>
    </section>
    <section class="maps-list">
      <h2>Maps</h2>
      <p class="typeface-summary">
        Discover Creator curated maps of the best spots in
        {{ place.fullName }}
      </p>
      <ul>
        <li v-for="map in maps" :key="map.id">
          <MPMapCard :mapId="map.id" />
        </li>
      </ul>
    </section>
  </div>
</template>

<style lang="postcss" scoped>
@import "@/assets/css/layouts.pcss";
@import "@/assets/css/breakpoints.pcss";

.destination {
  display: grid;
  gap: 1rem;
  margin-bottom: 5rem;
  @mixin screen md {
    margin-top: 8rem;
  }
}

.destination-header {
  @mixin layout-wrapper-wider;
  height: 16rem;
  background-size: cover;
  background-position: center;
  z-index: 1;
  box-shadow: var(--shadow-lg);
  @mixin screen md {
    height: 30rem;
    border-radius: 1rem;
  }
}

.destination-carousel-wrapper {
  background-color: var(--color-slate-50);
  padding: 6rem 0 2rem 0;
  position: relative;
  top: -5rem;
  > div {
    @mixin layout-wrapper-wider;
  }
}

.categories {
  @mixin layout-wrapper-wide;
  &:deep(.category-list) {
    width: 100vw;
    padding: 0 1rem;
    @mixin screen md {
      width: fit-content;
      padding: 0;
      margin: 0 auto;
    }
  }
}

.maps-list {
  @mixin layout-wrapper-wide;
  padding: 0 1rem;
  h2 {
    margin: 0;
  }
  p {
    color: var(--color-slate-500);
    margin-bottom: 2rem;
  }
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    display: grid;
    gap: 3rem;
    grid-template-columns: repeat(1, auto);
    @mixin screen xs {
      gap: 2rem;
      grid-template-columns: repeat(2, auto);
    }
    @mixin screen md {
      gap: 1.5rem;
      grid-template-columns: repeat(3, auto);
      li {
        margin-bottom: 1rem;
      }
    }
    @mixin screen lg {
      gap: 1.5rem;
      grid-template-columns: repeat(4, auto);
      li {
        margin-bottom: 1rem;
      }
    }
  }
}
</style>
